import { OpenInNew } from '@mui/icons-material'
import { IconButton, Tooltip, Typography, styled } from '@mui/material'
import { useTranslations } from 'next-intl'
import NextLink from 'next/link'
import { type ComponentProps, type ElementType, forwardRef } from 'react'

/**
 * Wraps Next.js Link with prefetch disabled by default. In production, Link
 * prefetch fires when links scroll into the viewport, and the resulting RSC
 * response can trigger Next.js's InnerScrollAndFocusHandler to reset
 * scrollTop to 0. Since our pages are client components, the prefetched RSC
 * payload is trivially small and not worth the scroll-reset risk.
 *
 * Callers can still opt in with `prefetch={true}` when needed.
 */
const Link = forwardRef<HTMLAnchorElement, ComponentProps<typeof NextLink>>(function Link(props, ref) {
  return <NextLink ref={ref} prefetch={false} {...props} />
})

type LinkProps = { noWrap?: boolean }

export default styled(Link, {
  shouldForwardProp: (prop) => prop !== 'noWrap' && prop !== 'sx'
})<LinkProps>(({ theme, noWrap }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  fontWeight: 600,
  ...(noWrap && {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  })
}))

// Pretends to be a link, but is not.
// The `component` prop type is declared explicitly because MUI's `styled()` does not
// preserve Typography's polymorphic `component` prop in the resulting component type.
export const LinkTypography = styled(Typography)<{ component?: ElementType }>(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  cursor: 'pointer'
}))

type OpenInNewTabIconProps = {
  href: string
}

export const OpenInNewTabIcon = ({ href }: OpenInNewTabIconProps) => {
  const t = useTranslations('components.Link')
  return (
    <Tooltip
      title={t('OpenInNewTabIcon_tooltip')}
      placement="top"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10]
              }
            }
          ]
        }
      }}
    >
      <IconButton LinkComponent={Link} href={href} target="_blank" color="accent">
        <OpenInNew fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}
